/* GASSISA
=========================================================================================
* Descripción: Catálogo de conceptos de bonificación/deducción de nómina (esquema global).
* Se persiste en la tabla Parametros (clave ESQUEMA_BONOS_NOMINA) y define las columnas
* dinámicas de la tabla de resultados de nómina.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 19/08/2026 | Lic. José Luis Aragón Cervantes  | Edita esquema global (parámetros), aplica a todos los empleados
*      3       | 21/08/2026 | Lic. José Luis Aragón Cervantes  | Agregar/quitar concepto es global, editar valor es por empleado
 *      4       | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Solo tipo+descripción en cabecera; Accordion colapsable; valores se editan en tabla
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, IconButton, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";

const TIPOS = [
    { value: "BONO", label: "Bonificación" },
    { value: "DEDUCCION", label: "Deducción" },
];

export default function ConceptosEmpleadoEditor({ esquema, onCambiarEsquema }) {
    const [expanded, setExpanded] = useState(false);
    const [tipo, setTipo] = useState("BONO");
    const [descripcion, setDescripcion] = useState("");

    const conceptos = Array.isArray(esquema) ? esquema : [];

    const agregarConcepto = () => {
        if (!descripcion.trim()) return;
        const nuevos = [...conceptos, { tipo, descripcion: descripcion.trim(), valor: 0 }];
        onCambiarEsquema(nuevos, true);
        setDescripcion("");
    };

    const quitarConcepto = (index) => {
        const nuevos = conceptos.filter((_, i) => i !== index);
        onCambiarEsquema(nuevos, true);
    };

    const handleKeyDownAgregar = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            agregarConcepto();
        }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            sx={{ width: "100%" }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5 } }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Conceptos de bonificación / deducción
                    </Typography>
                    <Chip label={`${conceptos.length} registrado(s)`} size="small" variant="outlined" />
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Define los conceptos que aparecerán como columnas en la tabla de resultados. Los valores se capturan por empleado directamente en la tabla.
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <TextField autoComplete="off"
                        select
                        label="Tipo"
                        size="small"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        sx={{ width: 150 }}
                    >
                        {TIPOS.map((opcion) => (
                            <MenuItem key={opcion.value} value={opcion.value}>
                                {opcion.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField autoComplete="off"
                        label="Descripción del concepto"
                        size="small"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        onKeyDown={handleKeyDownAgregar}
                        sx={{ flex: 1, minWidth: 180 }}
                    />
                    <Tooltip title="Agregar concepto al esquema global">
                        <span>
                            <IconButton onClick={agregarConcepto} color="primary" disabled={!descripcion.trim()}>
                                <AddCircleOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                <Box sx={{ maxHeight: 160, overflow: "auto" }}>
                    {conceptos.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            Sin conceptos registrados. Agrega uno arriba.
                        </Typography>
                    ) : (
                        conceptos.map((concepto, index) => {
                            const esBono = String(concepto.tipo || "").toUpperCase() === "BONO";
                            return (
                                <Stack
                                    key={index}
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ py: 0.3 }}
                                >
                                    <Chip
                                        label={esBono ? "BONO" : "DED"}
                                        size="small"
                                        color={esBono ? "success" : "error"}
                                        variant="outlined"
                                        sx={{ minWidth: 52 }}
                                    />
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {concepto.descripcion}
                                    </Typography>
                                    <Tooltip title="Quitar del esquema global">
                                        <IconButton size="small" onClick={() => quitarConcepto(index)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            );
                        })
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
